import React from 'react';
import { Box, Typography } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, RowSelectEventArgs, RowDataBoundEventArgs, SortDirection } from '@syncfusion/ej2-react-grids';
import { useNavigate } from 'react-router-dom';
import { TicketWorklistView } from '../../../../services/TicketService';
import TicketApprovalStatusChip from './TicketApprovalStatusChip';
import './WorklistGrid.css';

/**
 * Reusable grid component for displaying ticket worklist data
 * Can be used across different pages that need to display tickets in a grid format
 */
interface WorklistGridProps {
  tickets: TicketWorklistView[];
  searchText: string;
  showClosedTickets?: boolean; // Default false to hide closed/resolved tickets
}

const matchesSearchText = (ticket: TicketWorklistView, searchText: string): boolean => {
  const needle = searchText.toLowerCase();
  return (
    ticket.title.toLowerCase().includes(needle) ||
    ticket.category.toLowerCase().includes(needle) ||
    ticket.status.toLowerCase().includes(needle) ||
    ticket.assignedTo.toLowerCase().includes(needle) ||
    ticket.assignedToName.toLowerCase().includes(needle)
  );
};

const isClosedOrResolved = (status: string): boolean =>
  status === 'Closed' || status === 'Cancelled' || status === 'Resolved';

const WorklistGrid: React.FC<WorklistGridProps> = ({ tickets, searchText, showClosedTickets = false }) => {
  const navigate = useNavigate();

  // Filter tickets based on search text and closed/resolved status
  const filteredTickets = React.useMemo(() => {
    let filtered = tickets;

    // Filter out closed/cancelled/resolved tickets if showClosedTickets is false
    if (!showClosedTickets) {
      filtered = filtered.filter(ticket => !isClosedOrResolved(ticket.status));
    }

    // Apply search text filter
    if (searchText.trim() !== '') {
      filtered = filtered.filter(ticket => matchesSearchText(ticket, searchText));
    }

    return filtered;
  }, [tickets, searchText, showClosedTickets]);

  // Handle row click to navigate to edit page
  const handleRowSelect = (args: RowSelectEventArgs) => {
    const selectedTicket = args.data as TicketWorklistView;
    navigate(`/tickets/edit?id=${selectedTicket.id}`);
  };

  // Apply styling to closed/cancelled rows
  const handleRowDataBound = (args: RowDataBoundEventArgs) => {
    const ticket = args.data as TicketWorklistView;
    if (ticket.status === 'Closed') {
      args.row?.classList.add('closed-ticket');
    } else if (ticket.status === 'Cancelled') {
      args.row?.classList.add('cancelled-ticket');
    }
  };



  if (filteredTickets && filteredTickets.length > 0) {
    return (
      <Box>
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1, 
            color: '#64748b',
            fontStyle: 'italic'
          }}
        >
          Click on any row to edit the ticket
        </Typography>
        <GridComponent
          dataSource={filteredTickets}
          allowPaging={true}
          allowSorting={true}
          pageSettings={{ pageSize: 20 }}
          sortSettings={{
            columns: [{ field: 'createdDate', direction: 'Descending' as SortDirection }]
          }}
          height={600}
          gridLines="Both"
          rowSelected={handleRowSelect}
          rowDataBound={handleRowDataBound}
          cssClass="clickable-grid"
        >
        <ColumnsDirective>
          <ColumnDirective 
            field="id" 
            headerText="ID" 
            width="100"
            textAlign="Left"
            allowSorting={true}
          />
          <ColumnDirective 
            field="title" 
            headerText="Title" 
            width="200"
            textAlign="Left"
            allowSorting={true}
          />
          <ColumnDirective 
            field="category" 
            headerText="Category" 
            width="120"
            textAlign="Left"
            allowSorting={true}
          />
          <ColumnDirective 
            field="priority" 
            headerText="Priority" 
            width="100"
            textAlign="Center"
            allowSorting={true}
          />
          <ColumnDirective 
            field="urgency" 
            headerText="Urgency" 
            width="100"
            textAlign="Center"
            allowSorting={true}
          />
          <ColumnDirective 
            field="status" 
            headerText="Status" 
            width="120"
            textAlign="Center"
            allowSorting={true}
          />
          <ColumnDirective 
            field="approvalStatus" 
            headerText="Approval" 
            width="140"
            textAlign="Center"
            allowSorting={false}
            template={(props: TicketWorklistView) => <TicketApprovalStatusChip status={props.approvalStatus} />}
          />
          <ColumnDirective 
            field="assignedTo" 
            headerText="Assigned To" 
            width="150"
            textAlign="Left"
            allowSorting={true}
            template={(props: TicketWorklistView) => <>{props.assignedToName || props.assignedTo}</>}
          />
          <ColumnDirective 
            field="createdDate" 
            headerText="Created Date" 
            width="150"
            textAlign="Center"
            type="date"
            format="MM/dd/yyyy"
            allowSorting={true}
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      textAlign: 'center', 
      py: 4,
      color: '#64748b'
    }}>
      <Typography variant="h6" gutterBottom>
        {searchText ? 'No tickets match your search' : 'No tickets found'}
      </Typography>
      <Typography variant="body2">
        {searchText 
          ? 'Try adjusting your search criteria.' 
          : 'You haven\'t created any tickets yet.'
        }
      </Typography>
    </Box>
  );
};

export default WorklistGrid;
